import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { User, Clock, Wrench, Globe, Award, Briefcase, Box, Image as ImageIcon, Eye } from "lucide-react";
import { getUser } from "../services/profiles";
import { getRatingSummary } from "../services/reviews";
import { countCompletedCasesByDesigner } from "../services/cases";
import { RatingSummary } from "./StarRating";
import { StlViewer } from "./StlViewer";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import { isCadFile } from "../lib/cad";

const Row = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{label}</p>
        <p className="text-sm text-foreground">{value}</p>
      </div>
    </div>
  );
};

export const DesignerProfileModal = ({ designerId, open, onOpenChange, onSelect, canSelect, selecting }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [rating, setRating] = useState({ avg: 0, count: 0 });
  const [completed, setCompleted] = useState(0);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null); // { kind: 'image'|'cad', url, name } — gallery lightbox / 3D preview

  useEffect(() => {
    if (!open || !designerId) return;
    setLoading(true);
    (async () => {
      const [u, r, c] = await Promise.all([
        getUser(designerId),
        getRatingSummary(designerId),
        countCompletedCasesByDesigner(designerId),
      ]);
      setData(u);
      setRating(r);
      setCompleted(c);
      setLoading(false);
    })();
  }, [open, designerId]);

  const p = data?.designerProfile || {};
  const items = Array.isArray(p.portfolioItems) ? p.portfolioItems : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="designer-profile-modal">
        <DialogHeader>
          <DialogTitle>{t("profile.viewProfile")}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="space-y-3"><Skeleton className="h-24 w-full" /><Skeleton className="h-32 w-full" /></div>
        ) : !data ? (
          <p className="text-sm text-muted-foreground">—</p>
        ) : (
          <div className="space-y-5">
            <div className="flex items-center gap-4">
              {p.photo ? (
                <img src={p.photo} alt={data.name} className="h-16 w-16 rounded-full border border-border object-cover" data-testid="designer-photo" />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary text-muted-foreground"><User className="h-7 w-7" /></div>
              )}
              <div>
                <h3 className="font-heading text-xl font-semibold tracking-tight" data-testid="designer-name">{data.name}</h3>
                <div className="mt-1"><RatingSummary avg={rating.avg} count={rating.count} testid="designer-modal-rating" /></div>
              </div>
            </div>

            {p.description && <p className="text-sm leading-relaxed text-muted-foreground">{p.description}</p>}

            <div className="grid grid-cols-2 gap-4">
              <Row icon={Briefcase} label={t("profile.experience")} value={p.experience} />
              <Row icon={Wrench} label={t("profile.software")} value={p.software} />
              <Row icon={Award} label={t("profile.specialties")} value={p.specialties} />
              <Row icon={Globe} label={t("profile.languages")} value={p.languages} />
              <Row icon={Clock} label={t("profile.avgDelivery")} value={p.turnaround} />
              <Row icon={Award} label={t("profile.completedCases")} value={String(completed)} />
            </div>

            <div>
              <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold"><ImageIcon className="h-4 w-4" /> {t("profile.portfolioSection")}</p>
              {items.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="portfolio-empty">{p.portfolio || t("profile.noPortfolio")}</p>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-2" data-testid="portfolio-grid">
                    {items.map((it, i) => {
                      const cad = it.type === "cad" || isCadFile(it.name);
                      return cad ? (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setPreview({ kind: "cad", url: it.url, name: it.name })}
                          className="group relative flex h-24 flex-col items-center justify-center gap-1 rounded-md border border-border bg-secondary/40 p-2 text-center text-xs text-muted-foreground transition-colors hover:border-brand hover:text-brand"
                          data-testid={`portfolio-item-${i}`}
                        >
                          <Box className="h-5 w-5" />
                          <span className="truncate w-full">{it.name}</span>
                          <span className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-md bg-black/0 opacity-0 transition-opacity group-hover:bg-black/40 group-hover:opacity-100">
                            <Eye className="h-5 w-5 text-white" />
                          </span>
                        </button>
                      ) : (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setPreview({ kind: "image", url: it.url, name: it.name })}
                          className="group relative overflow-hidden rounded-md border border-border"
                          data-testid={`portfolio-item-${i}`}
                        >
                          <img src={it.url} alt={it.name} className="h-24 w-full object-cover" loading="lazy" />
                          <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-opacity group-hover:bg-black/30 group-hover:opacity-100">
                            <Eye className="h-5 w-5 text-white" />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-2 text-[11px] text-muted-foreground">{t("profile.galleryHint")}</p>
                </>
              )}
            </div>

            {canSelect && onSelect && (
              <Button size="lg" className="w-full rounded-full bg-brand text-base font-semibold text-white hover:bg-brand/90" onClick={onSelect} disabled={selecting} data-testid="modal-select-designer">
                {t("applications.select")}
              </Button>
            )}
          </div>
        )}

        {/* Gallery preview: enlarge images / view CAD in 3D. No downloads — portfolio
            CAD files (STL/PLY/OBJ) are shown as a read-only 3D preview only. */}
        <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
          <DialogContent className="max-w-3xl" data-testid="portfolio-preview-dialog" onContextMenu={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle className="truncate text-sm">{preview?.name}</DialogTitle>
            </DialogHeader>
            {preview?.kind === "cad" ? (
              <StlViewer url={preview.url} name={preview.name} />
            ) : preview ? (
              <img src={preview.url} alt={preview.name} className="max-h-[70vh] w-full rounded-md object-contain" draggable={false} data-testid="portfolio-preview-image" />
            ) : null}
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};
